import { ChangeDetectionStrategy, Component, computed, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";

import { filter, map, startWith } from "rxjs/operators";

import { ThemeService } from "@core/services/theme.service";
import { HeaderComponent } from "@features/common/header/header.component";
import { TokensWarningBannerComponent } from "@features/common/tokens-warning-banner/tokens-warning-banner.component";
import { LoginRoutesConstants } from "@login/routes/login.routes.constants";
import { ToastComponent } from "@shared/components/toast/toast.component";

@Component({
  selector: "component-app",
  imports: [RouterOutlet, ToastComponent, HeaderComponent, TokensWarningBannerComponent],
  template: `
    <div class="beta-label">BETA</div>
    <div class="app-container">
      @if (shouldShowHeader()) {
        <component-header />
      }
      <component-tokens-warning-banner />
      <div class="app-content">
        <div class="full-size-component">
          <router-outlet />
        </div>
      </div>
      <footer class="app-footer">Geekle AI Labs 910 Foulk Road, Suite 201 Wilmington, DE 19803, USA</footer>
    </div>
    <component-toast />
  `,
  styleUrls: ["./app.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent {
  private readonly _themeService = inject(ThemeService);
  private readonly _router = inject(Router);

  private readonly _currentUrl = toSignal(
    this._router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => (event as NavigationEnd).url),
      startWith(this._router.url)
    ),
    { initialValue: this._router.url }
  );

  protected readonly shouldShowHeader = computed(() => {
    const url = this._currentUrl();

    // Hide header on login-related routes and public document viewer
    const hideHeaderRoutes = [
      `/${LoginRoutesConstants.LOGIN}`,
      `/${LoginRoutesConstants.SIGNUP}`,
      `/${LoginRoutesConstants.REGISTER}`,
      `/${LoginRoutesConstants.ERROR}`,
      `/${LoginRoutesConstants.AUTH_CALLBACK}`,
      `/${LoginRoutesConstants.TERMS}`,
      `/${LoginRoutesConstants.PRIVACY_POLICY}`
    ];

    return url !== "/" && !hideHeaderRoutes.some((route) => url.startsWith(route));
  });

  constructor() {
    // Initialize application-wide services
    this._themeService.initializeTheme();
  }
}
